import { xget, xhttp } from "../Fetchv2.js";
import { PGST } from "../schemas/pg.schema-types.js";
import { PGSchema } from "../schemas/pg.schema.js";

export type PerformanceReviewCreateBody = Pick<PGSchema.hcm_performance_reviews, "title" | "start_date" | "end_date" | "review_types"> &
	Partial<
		Omit<
			PGSchema.hcm_performance_reviews,
			"id" | "created_at" | "updated_at" | "pgid" | "title" | "start_date" | "end_date" | "review_types" | "psm_page" | "psm_performance_review_subjects" | "psm_performance_review_feedbacks"
		>
	>;

export type PerformanceReviewUpdateBody = Partial<
	Omit<PGSchema.hcm_performance_reviews, "id" | "created_at" | "updated_at" | "pgid" | "psm_page" | "psm_performance_review_subjects" | "psm_performance_review_feedbacks">
>;

export type FeedbackCreateBody = {
	subject_id: PGSchema.hcm_performance_review_subjects["id"];
	evaluator_id: string;
	role: PGST.hcm_performance_review_feedbacks.role;
};

export type FeedbackUpdateBody = Partial<Pick<PGSchema.hcm_performance_review_feedbacks, "status" | "answers">>;

export class PerformanceReviews {
	// #region ── Performance Reviews ──────────────────────────────────────

	/** @GET /pages/:pgid/performance-reviews */
	async list(pgid: PGSchema.pages["id"], token?: string) {
		return xget<PGSchema.hcm_performance_reviews[]>(`/pages/${pgid}/performance-reviews`, undefined, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	/** @GET /pages/:pgid/performance-reviews/:reviewId */
	async getById(pgid: PGSchema.pages["id"], reviewId: PGSchema.hcm_performance_reviews["id"], token?: string) {
		return xget<PGSchema.hcm_performance_reviews>(`/pages/${pgid}/performance-reviews/${reviewId}`, undefined, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	/** @POST /pages/:pgid/performance-reviews */
	async create(pgid: PGSchema.pages["id"], data: PerformanceReviewCreateBody, token?: string) {
		return xhttp<PGSchema.hcm_performance_reviews>(`/pages/${pgid}/performance-reviews`, "POST", data, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	/** @PUT /pages/:pgid/performance-reviews/:reviewId */
	async update(pgid: PGSchema.pages["id"], reviewId: PGSchema.hcm_performance_reviews["id"], data: PerformanceReviewUpdateBody, token?: string) {
		return xhttp<PGSchema.hcm_performance_reviews>(`/pages/${pgid}/performance-reviews/${reviewId}`, "PUT", data, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	/** @PUT /pages/:pgid/performance-reviews/:reviewId/launch */
	async launch(pgid: PGSchema.pages["id"], reviewId: PGSchema.hcm_performance_reviews["id"], data?: { end_date?: string; start_date?: string }, token?: string) {
		return xhttp<PGSchema.hcm_performance_reviews>(`/pages/${pgid}/performance-reviews/${reviewId}/launch`, "PUT", data ?? {}, { headers: token ? { "app-token": token } : undefined });
	}

	/** @PUT /pages/:pgid/performance-reviews/:reviewId/stop */
	async stop(pgid: PGSchema.pages["id"], reviewId: PGSchema.hcm_performance_reviews["id"], token?: string) {
		return xhttp<PGSchema.hcm_performance_reviews>(`/pages/${pgid}/performance-reviews/${reviewId}/stop`, "PUT", {}, { headers: token ? { "app-token": token } : undefined });
	}

	/** @DELETE /pages/:pgid/performance-reviews/:reviewId */
	async delete(pgid: PGSchema.pages["id"], reviewId: PGSchema.hcm_performance_reviews["id"], token?: string) {
		return xhttp<{ deleted: boolean }>(
			`/pages/${pgid}/performance-reviews/${reviewId}`,
			"DELETE",
			{},
			{
				headers: token ? { "app-token": token } : undefined
			}
		);
	}

	// #endregion

	// #region ── Subjects ─────────────────────────────────────────────────

	/** @GET /pages/:pgid/performance-reviews/:reviewId/subjects */
	async listSubjects(pgid: PGSchema.pages["id"], reviewId: PGSchema.hcm_performance_reviews["id"], token?: string) {
		return xget<PGSchema.hcm_performance_review_subjects[]>(`/pages/${pgid}/performance-reviews/${reviewId}/subjects`, undefined, { headers: token ? { "app-token": token } : undefined });
	}

	/** @POST /pages/:pgid/performance-reviews/:reviewId/subjects */
	async addSubject(pgid: PGSchema.pages["id"], reviewId: PGSchema.hcm_performance_reviews["id"], subject_id: string, token?: string) {
		return xhttp<PGSchema.hcm_performance_review_subjects>(
			`/pages/${pgid}/performance-reviews/${reviewId}/subjects`,
			"POST",
			{ subject_id },
			{ headers: token ? { "app-token": token } : undefined }
		);
	}

	/** @DELETE /pages/:pgid/performance-reviews/:reviewId/subjects/:subjectId */
	async removeSubject(pgid: PGSchema.pages["id"], reviewId: PGSchema.hcm_performance_reviews["id"], subjectId: PGSchema.hcm_performance_review_subjects["id"], token?: string) {
		return xhttp<{ deleted: boolean }>(`/pages/${pgid}/performance-reviews/${reviewId}/subjects/${subjectId}`, "DELETE", {}, { headers: token ? { "app-token": token } : undefined });
	}

	/** @PUT /pages/:pgid/performance-reviews/:reviewId/subjects/:subjectId/feedback */
	async updateSubjectFeedback(
		pgid: PGSchema.pages["id"],
		reviewId: PGSchema.hcm_performance_reviews["id"],
		subjectId: PGSchema.hcm_performance_review_subjects["id"],
		data: { admin_feedback?: string; action_plan?: { id: string; task: string; due_date?: string; status: "pending" | "in_progress" | "completed" }[] },
		token?: string
	) {
		return xhttp<PGSchema.hcm_performance_review_subjects>(`/pages/${pgid}/performance-reviews/${reviewId}/subjects/${subjectId}/feedback`, "PUT", data, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	// #endregion

	// #region ── Templates ───────────────────────────────────────────────

	/** @GET /pages/:pgid/performance-review-templates?type=predefined|custom|mine */
	async listTemplates(pgid: PGSchema.pages["id"], type?: "predefined" | "custom" | "mine", token?: string) {
		return xget<PGSchema.hcm_performance_review_templates[]>(`/pages/${pgid}/performance-review-templates`, type ? { type } : undefined, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	/** @POST /pages/:pgid/performance-reviews/from-template/:templateId */
	async createFromTemplate(pgid: PGSchema.pages["id"], templateId: PGSchema.hcm_performance_review_templates["id"], token?: string) {
		return xhttp<PGSchema.hcm_performance_reviews>(
			`/pages/${pgid}/performance-reviews/from-template/${templateId}`,
			"POST",
			{},
			{
				headers: token ? { "app-token": token } : undefined
			}
		);
	}

	/** @DELETE /pages/:pgid/performance-review-templates/:templateId */
	async deleteTemplate(pgid: PGSchema.pages["id"], templateId: PGSchema.hcm_performance_review_templates["id"], token?: string) {
		return xhttp<{ deleted: boolean }>(
			`/pages/${pgid}/performance-review-templates/${templateId}`,
			"DELETE",
			{},
			{
				headers: token ? { "app-token": token } : undefined
			}
		);
	}

	/** @POST /pages/:pgid/performance-reviews/:reviewId/save-as-template */
	async saveAsTemplate(pgid: PGSchema.pages["id"], reviewId: PGSchema.hcm_performance_reviews["id"], data?: { title?: string; description?: string | null }, token?: string) {
		return xhttp<PGSchema.hcm_performance_review_templates>(`/pages/${pgid}/performance-reviews/${reviewId}/save-as-template`, "POST", data ?? {}, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	// #endregion

	// #region ── My Reviews (employee-facing) ────────────────────────────

	/** @GET /pages/:pgid/my-performance-reviews */
	async getMyReviews(pgid: PGSchema.pages["id"], token?: string) {
		return xget<{
			feedbacks: PGSchema.hcm_performance_review_feedbacks[];
			reviews: PGSchema.hcm_performance_reviews[];
			myResultReviews: PGSchema.hcm_performance_reviews[];
			teamResultReviews: PGSchema.hcm_performance_reviews[];
		}>(`/pages/${pgid}/my-performance-reviews`, undefined, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	/** @GET /pages/:pgid/my-performance-reviews/:reviewId/results */
	async getMyReviewResults(pgid: PGSchema.pages["id"], reviewId: PGSchema.hcm_performance_reviews["id"], token?: string) {
		return xget<{
			review: PGSchema.hcm_performance_reviews;
			subjects: PGSchema.hcm_performance_review_subjects[];
			feedbacks: PGSchema.hcm_performance_review_feedbacks[];
			role: "manager" | "subject";
		}>(`/pages/${pgid}/my-performance-reviews/${reviewId}/results`, undefined, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	/** @PUT /pages/:pgid/my-performance-reviews/:feedbackId */
	async updateMyFeedback(pgid: PGSchema.pages["id"], feedbackId: PGSchema.hcm_performance_review_feedbacks["id"], data: FeedbackUpdateBody, token?: string) {
		return xhttp<PGSchema.hcm_performance_review_feedbacks>(`/pages/${pgid}/my-performance-reviews/${feedbackId}`, "PUT", data, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	// #endregion

	// #region ── Feedbacks ────────────────────────────────────────────────

	/** @GET /pages/:pgid/performance-reviews/:reviewId/feedbacks */
	async listFeedbacks(pgid: PGSchema.pages["id"], reviewId: PGSchema.hcm_performance_reviews["id"], token?: string) {
		return xget<PGSchema.hcm_performance_review_feedbacks[]>(`/pages/${pgid}/performance-reviews/${reviewId}/feedbacks`, undefined, { headers: token ? { "app-token": token } : undefined });
	}

	/** @POST /pages/:pgid/performance-reviews/:reviewId/feedbacks */
	async createFeedback(pgid: PGSchema.pages["id"], reviewId: PGSchema.hcm_performance_reviews["id"], data: FeedbackCreateBody, token?: string) {
		return xhttp<PGSchema.hcm_performance_review_feedbacks>(`/pages/${pgid}/performance-reviews/${reviewId}/feedbacks`, "POST", data, { headers: token ? { "app-token": token } : undefined });
	}

	/** @PUT /pages/:pgid/performance-reviews/:reviewId/feedbacks/:feedbackId */
	async updateFeedback(
		pgid: PGSchema.pages["id"],
		reviewId: PGSchema.hcm_performance_reviews["id"],
		feedbackId: PGSchema.hcm_performance_review_feedbacks["id"],
		data: FeedbackUpdateBody,
		token?: string
	) {
		return xhttp<PGSchema.hcm_performance_review_feedbacks>(`/pages/${pgid}/performance-reviews/${reviewId}/feedbacks/${feedbackId}`, "PUT", data, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	/** @DELETE /pages/:pgid/performance-reviews/:reviewId/feedbacks/:feedbackId */
	async deleteFeedback(pgid: PGSchema.pages["id"], reviewId: PGSchema.hcm_performance_reviews["id"], feedbackId: PGSchema.hcm_performance_review_feedbacks["id"], token?: string) {
		return xhttp<{ deleted: boolean }>(`/pages/${pgid}/performance-reviews/${reviewId}/feedbacks/${feedbackId}`, "DELETE", {}, { headers: token ? { "app-token": token } : undefined });
	}

	// #endregion
}
